/*!
 * Dalafisk – header behaviour
 * ------------------------------------------------------------------
 * Three behaviours ported from the HTML design:
 *   1. mega menus opening from the pill nav
 *   2. the search field collapsing to an icon and expanding inline
 *   3. the header shrinking on scroll (the logo "sign" tucks up)
 *
 * State lives in attributes on the header root, matching the CSS:
 *   [data-dalafisk-shrunk]  – scrolled past the threshold
 *   [data-dalafisk-search]  – search field expanded
 *   [data-dalafisk-open]    – on an individual mega panel
 *
 * Everything here is progressive: with the script absent the nav links still
 * work (a mega's parent is a button, so its children stay reachable via the
 * mobile drawer and the collection pages), search still submits, and the
 * header simply never shrinks.
 */
(function () {
  'use strict';

  if (window.__dalafiskHeader) return;
  window.__dalafiskHeader = true;

  var SHRUNK = 'data-dalafisk-shrunk';
  var SEARCH = 'data-dalafisk-search';
  // set while the panel is showing the empty state rather than results
  var SEARCH_IDLE = 'data-dalafisk-search-idle';
  // set while the FIRST results for a query are still on their way
  var SEARCH_BUSY = 'data-dalafisk-search-busy';
  var OPEN = 'data-dalafisk-open';
  /* One frame of `transition: none` while one mega hands over to the next. */
  var INSTANT = 'data-dalafisk-instant';

  function init() {
    var header = document.querySelector('.header.customHeaderStyle');
    if (!header) return;

    /* ---------- mega menus ----------
       The design opens these on HOVER and keeps click as the second way in:
         rad.addEventListener('mouseenter', oppna);
         rad.addEventListener('mouseleave', stangSnart);   // 160ms grace
       `rad` is the <li>, and the panel is inside it, so moving the pointer off
       the pill and down onto the panel never leaves the element — mouseleave
       only fires once the pointer is off BOTH.

       The grace period is what makes it usable: without it, the diagonal from
       one pill to a tile in the open panel clips the gap and the menu shuts
       under the cursor.

       Bound only where there is a real pointer. On touch, mouseenter fires on
       the tap that is already going to activate the button, so the two would
       race; the click handler below serves touch and keyboard alike. */
    var toggles = header.querySelectorAll('[data-dalafisk-menu]');
    var canHoverMenus = !window.matchMedia || window.matchMedia('(hover: hover)').matches;
    var megaCloseTimer = null;

    function closeAll(except) {
      Array.prototype.forEach.call(toggles, function (t) {
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (!panel || panel === except) return;
        panel.removeAttribute(OPEN);
        t.setAttribute('aria-expanded', 'false');
      });
    }

    /* Handing over from one menu to another, the design's `data-direkt`: the
       outgoing panel must not play its .28s collapse while the incoming one
       plays its .6s reveal, or the row visibly shuts and reopens. The attribute
       kills the transition for exactly one frame — the reflow between setting
       and clearing it is what makes the browser see `transition: none`. */
    function closeAllInstant(except) {
      Array.prototype.forEach.call(toggles, function (t) {
        var panel = document.getElementById(t.getAttribute('aria-controls'));
        if (!panel || panel === except || !panel.hasAttribute(OPEN)) return;
        panel.setAttribute(INSTANT, '');
        panel.removeAttribute(OPEN);
        t.setAttribute('aria-expanded', 'false');
        void panel.offsetWidth;
        requestAnimationFrame(function () { panel.removeAttribute(INSTANT); });
      });
    }

    function openMega(toggle, panel) {
      clearTimeout(megaCloseTimer);
      if (panel.hasAttribute(OPEN)) return;
      closeAllInstant(panel);
      /* The design's `oppna()` closes the search first — the panel and the
         expanded field occupy the same row. */
      closeSearch();
      panel.setAttribute(OPEN, '');
      toggle.setAttribute('aria-expanded', 'true');
    }

    function closeMegaSoon() {
      clearTimeout(megaCloseTimer);
      megaCloseTimer = setTimeout(function () { closeAll(null); }, 160);
    }

    Array.prototype.forEach.call(toggles, function (toggle) {
      var panel = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!panel) return;
      var item = toggle.parentElement;

      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        clearTimeout(megaCloseTimer);
        /* Where there is a pointer, hover already owns opening AND closing, so
           the design's click handler only ever opens — `knapp.addEventListener
           ('click', … oppna())`. Toggling here instead shut the panel that the
           hover under the very same cursor had just opened, which reads as the
           click doing nothing. Escape and clicking away still close it.

           Without hover the click is the only control there is, so then it
           toggles. */
        if (!canHoverMenus && panel.hasAttribute(OPEN)) {
          panel.removeAttribute(OPEN);
          toggle.setAttribute('aria-expanded', 'false');
          return;
        }
        openMega(toggle, panel);
      });

      if (canHoverMenus && item) {
        item.addEventListener('mouseenter', function () { openMega(toggle, panel); });
        item.addEventListener('mouseleave', closeMegaSoon);
      }

      /* Following a link inside the panel closes it, so coming back with the
         browser's Back button does not land on a menu still hanging open. */
      panel.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('a')) closeAll(null);
      });
    });

    // Clicking away or pressing Escape closes whatever is open.
    /* Clicking away closes the SEARCH too, not just the cart and the menu.
       `closeAll` handles the other two; the search has its own closer and was
       never called here — only from the Escape handler below. On a phone that
       left no way out at all: the search toggle collapses to `width: 0` while
       search is open (it is the field's own space), so once the panel was up,
       tapping outside did nothing and the toggle could not be tapped either.
       Reproduced at 390px — after typing, `data-dalafisk-search` was still set
       and the panel still 320x730 after a tap well below the header. */
    document.addEventListener('click', function (e) {
      if (!header.contains(e.target)) {
        closeAll(null);
        closeSearch();
      }
    });
    header.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        closeAll(null);
        closeSearch();
      }
    });

    /* Escape has the same leak, from the other direction: Pinnacle's own
       handler (header.js:28-35) listens on DOCUMENT and clears `open` and
       `.active` but not `body.overflow-hidden`, so Escape closed the menu and
       left the page unscrollable. The handler above only sees the key when
       focus is inside the header, which it need not be — hence a document-level
       one here.

       It closes the CART too, which is the design's own `stangAllt()`:
         addEventListener('keydown', e => { if (e.key === 'Escape') stangAllt(); })
       Pinnacle's <cart-drawer> only answers Escape when focus is already inside
       it, so pressing Escape after opening the cart from the pill did nothing —
       measured, the drawer stayed open. That was survivable while the page
       still scrolled behind it; now that the panel takes the design's body lock
       (assets/dalafisk-cart.js), a dead Escape means a visitor who reaches for
       it is left looking at a page that will not move. Outside-click and the
       pill both still close it as before.

       All three are idempotent, so overlapping handlers are harmless. */
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      closeMobileMenu();
      closeCart();
      closeSearch();
    });

    /* ---------- collapsing search ---------- */
    var searchToggle = header.querySelector('[data-dalafisk-search-toggle]');
    var searchForm = header.querySelector('.searchform');

    var emptyState = header.querySelector('[data-dalafisk-search-empty]');
    var RECENT_KEY = 'dalafisk:recent-searches';
    var RECENT_MAX = 4;

    function searchInput() {
      return searchForm && searchForm.querySelector('input[type="search"], input[name="q"]');
    }

    /* localStorage can throw outright in private mode, so every read and write
       is guarded and simply degrades to "no history". */
    function readRecent() {
      try {
        var raw = JSON.parse(localStorage.getItem(RECENT_KEY));
        return Object.prototype.toString.call(raw) === '[object Array]' ? raw : [];
      } catch (e) {
        return [];
      }
    }

    function rememberSearch(q) {
      q = (q || '').trim();
      if (!q) return;
      var list = readRecent().filter(function (x) {
        return String(x).toLowerCase() !== q.toLowerCase();
      });
      list.unshift(q);
      try {
        localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
      } catch (e) {}
    }

    function renderRecent() {
      if (!emptyState) return;
      var list = emptyState.querySelector('[data-dalafisk-recent]');
      var none = emptyState.querySelector('[data-dalafisk-recent-none]');
      if (!list) return;
      var previous = list.querySelectorAll('[data-dalafisk-recent-item]');
      for (var i = 0; i < previous.length; i++) previous[i].parentNode.removeChild(previous[i]);
      var recent = readRecent();
      if (none) none.hidden = recent.length > 0;
      var form = searchForm && searchForm.querySelector('form');
      var action = (form && form.getAttribute('action')) || '/search';
      recent.forEach(function (q) {
        var li = document.createElement('li');
        li.setAttribute('data-dalafisk-recent-item', '');
        var a = document.createElement('a');
        a.className = 'dalafisk-search-empty__row';
        a.href = action + '?q=' + encodeURIComponent(q);
        a.textContent = q;
        li.appendChild(a);
        list.appendChild(li);
      });
    }

    /* ---------- empty state -> results, with nothing in between ----------
       The empty state stands in for Pinnacle's panel, which renders nothing
       until there is input. It used to hand over the moment a character was
       typed — and that left a HOLE, because nothing arrives for a while yet:
       Pinnacle debounces the field by 300ms and the request takes a few hundred
       more. Measured at just over a second on a warm dev server, longer over the
       network. What the visitor saw was an empty white panel, i.e. the search
       dying and then coming back; reported after clicking a "Populärt just nu"
       row, which fills the field programmatically and hits exactly the same
       path.

       So the trigger is no longer "has something been typed" but "are the
       results actually here". The empty state stays up, dimmed with a spinner
       (custom-header-style.css), until the panel holds a rendered result set. */
    var resultsPanel = header.querySelector('.thb-predictive-search');
    /* Pinnacle never clears the panel, so after a close its markup is still the
       PREVIOUS query's. Without this a reopen would hand straight over to stale
       results for the split second before the new ones land. */
    var panelStale = true;
    var awaitingResults = false;
    var awaitTimer = null;

    function panelHasResults() {
      // The footer is rendered only when predictive_search.performed is true,
      // so it is present for a no-hits answer as well as a full one.
      return !panelStale && !!(resultsPanel && resultsPanel.querySelector('.thb-predictive-search--footer'));
    }

    /* Called wherever a fetch is about to happen. The timer is a safety net: if
       the request never answers, hand over rather than spin forever. */
    function awaitResults() {
      awaitingResults = true;
      clearTimeout(awaitTimer);
      awaitTimer = setTimeout(function () {
        awaitingResults = false;
        syncEmptyState();
      }, 6000);
    }

    function stopAwaiting() {
      awaitingResults = false;
      clearTimeout(awaitTimer);
    }

    function syncEmptyState() {
      if (!emptyState) return;
      var input = searchInput();
      var typed = !!input && input.value.trim() !== '';
      if (!typed) stopAwaiting();

      var waiting = typed && awaitingResults && !panelHasResults();
      var show = header.hasAttribute(SEARCH) && (!typed || waiting);

      if (show) renderRecent();
      emptyState.hidden = !show;
      /* Pinnacle's panel element stays in the DOM with no results in it, and it
         sits a layer above this one — so an empty white box covered the top of
         the empty state. The flag lets CSS take it out while this one is up,
         and hand the layer back once there is something to show. */
      if (show) {
        header.setAttribute(SEARCH_IDLE, '');
      } else {
        header.removeAttribute(SEARCH_IDLE);
      }
      if (waiting) {
        header.setAttribute(SEARCH_BUSY, '');
      } else {
        header.removeAttribute(SEARCH_BUSY);
      }
    }

    /* ---------- one way in to "search for this, NOW" ----------
       Pinnacle only ever fetches off the field's `input` event, debounced by
       300ms (predictive-search.js:24). So a term that arrives in the field
       without being typed fetched nothing at all, and the panel opened with
       nothing in it. Two routes do that:

         * a "Populärt just nu" row, which fills the field programmatically
         * REOPENING ON A RESULTS PAGE, where the field is already carrying
           `search.terms` from the markup — the field expanded and no drawer came
           with it. Measured: a 414x88 panel with 348 bytes of empty container.

       Pinnacle has an `onFocus` written for exactly this second case and never
       wires it up (predictive-search.js:63-71).

       `<search-form>` is the element itself, so the fetch can be called straight
       out. That also skips the debounce, which a deliberate click or an open
       should never have to pay — dispatching `input` instead would only queue
       the same request 300ms later. */
    function runSearch(term) {
      term = (term || '').trim();
      if (!term) return;
      awaitResults();
      if (searchForm && typeof searchForm.getSearchResults === 'function') {
        if (typeof searchForm.open === 'function') searchForm.open();
        searchForm.getSearchResults(term);
      } else {
        var input = searchInput();
        if (input) input.dispatchEvent(new Event('input', { bubbles: true }));
      }
      syncEmptyState();
    }

    /* Pinnacle replaces the panel's contents in one assignment when the request
       answers (predictive-search.js:98), so watching its children is all it
       takes to know the results have landed — no hook into its code. */
    if (resultsPanel && window.MutationObserver) {
      new MutationObserver(function () {
        panelStale = false;
        stopAwaiting();
        syncEmptyState();
      }).observe(resultsPanel, { childList: true });
    }

    /* CLOSING CLEARS THE FIELD, so every reopen starts from the same place.
       It did not, and which state you came back to depended on HOW you had
       closed: Escape happened to clear the input (Pinnacle's own handler), the
       toggle and the outside click did not. So reopening after a search could
       show the previous query's results, a stale panel over an empty field, or
       the empty state — the inconsistency reported.

       Pinnacle's `search-form` element owns the reset (it drives the predictive
       fetch off the input), so the input is cleared and an `input` event
       dispatched rather than the results being torn out by hand — that is what
       makes the panel let go of the old query. */
    function closeSearch() {
      header.removeAttribute(SEARCH);
      header.removeAttribute(SEARCH_IDLE);
      header.removeAttribute(SEARCH_BUSY);
      if (searchToggle) searchToggle.setAttribute('aria-expanded', 'false');

      var input = searchInput();
      if (input && input.value !== '') {
        input.value = '';
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }

      /* Part of the same reset: the panel keeps the closed query's markup, and
         the next open must not flash it before its own results arrive. */
      stopAwaiting();
      panelStale = true;

      if (emptyState) emptyState.hidden = true;

      /* AND THE PAGE HAS TO BE GIVEN BACK. Same trap as the mobile menu below:
         Pinnacle locks the page when the search opens — focusing the field runs
         `<search-form>.open()`, which sets `body.open-search`, and app.css:1020
         makes that `overflow: hidden`. Only Pinnacle's own `close()` takes it
         off again, and this never called it.

         It LOOKED fine whenever something had been typed, because clearing the
         input above fires `input`, and Pinnacle's debounced handler drops the
         class 300ms later when it sees an empty term. Close an empty search —
         open it, change your mind, tap the X — and nothing fired: the panel
         went away and the page could not be scrolled again until a reload.
         Reproduced at 375px: after the X, `body.open-search` was still set and
         `scrollBy(0, 400)` moved the page 0px. That is the phone's "buggar".

         Pinnacle's component owns the lock, so it is asked to let go rather
         than having the class pulled out from under it — the same way the cart
         drawer is closed through its own `close()`. It is idempotent, and it
         also restores the `inert` that `open()` removed, which was likewise
         never being put back: the closed results panel stayed in the tab
         order. */
      if (searchForm && typeof searchForm.close === 'function') searchForm.close();
    }

    /* ---------- only one overlay at a time ----------
       The three overlays are independent components: Pinnacle's <cart-drawer>,
       its mobile-menu <details>, and our search. Each already closes on an
       outside click, but "outside" for the search means outside the HEADER —
       and both the cart button and the menu toggle live INSIDE the header, so
       opening either of them left the search sitting open behind it. Closing
       the other two whenever one opens is the only thing that makes them
       mutually exclusive; there is no shared controller to hang it off.

       Pinnacle's drawer is driven through its own public API (`close()` /
       `isOpen`, and it announces itself with `cart-drawer:open` on document —
       app.js:399). Nothing in Pinnacle is modified. */
    function cartDrawerEl() { return document.querySelector('cart-drawer'); }
    function mobileMenuEl() { return document.querySelector('details.mobile-toggle-wrapper'); }

    function closeCart() {
      var cd = cartDrawerEl();
      if (cd && cd.isOpen && typeof cd.close === 'function') cd.close();
    }
    /* CLOSING THE DRAWER IS THREE THINGS, NOT ONE.
       Pinnacle locks the page when the menu opens — `document.body.classList
       .add('overflow-hidden')` — and the ONLY code that takes that lock off
       again is its own hamburger click handler (header.js:39-52), which also
       clears `.active` on the wrapper. Anything else that closes the drawer
       leaves both behind: the panel slides away, `body` keeps
       `overflow: hidden`, AND THE PAGE CANNOT BE SCROLLED. The stale `.active`
       makes it worse — the next tap on the hamburger takes the "already open"
       branch, so it spends itself resetting state instead of reopening the
       menu, and the menu appears to ignore the first tap.

       Every route into here hit that: the drawer's own X (on both levels), its
       search row, and the header's search toggle. Reproduced on a phone
       viewport — after closing with the X, `body.overflow-hidden` was still set
       and a wheel scroll moved the page 0px.

       So this undoes all three, and is the single place the rest of the file
       calls. Pinnacle's own handler stays untouched and remains idempotent:
       removing a class that is not there is a no-op. */
    function closeMobileMenu() {
      var d = mobileMenuEl();
      if (d) {
        d.open = false;
        d.classList.remove('active');
      }
      document.body.classList.remove('overflow-hidden');
      resetMobileMenuLevel(d);
    }

    /* CLOSING FROM LEVEL 2 USED TO REOPEN ON LEVEL 2.
       The submenu is held open by `details.link-container[open].menu-opening`,
       and only the back button clears it (header.js:124-131). Close the drawer
       from inside a submenu — which the design's second-level X now makes easy —
       and that state survives, so the next open lands on the sub-list instead of
       the top level, with the search row and the main rows hidden behind it.
       Verified: after closing from level 2, `menu-opening` was still set and the
       reopened panel was showing the submenu.

       The reset waits for the panel to finish sliding out (.38s, matching the
       transform transition) so the levels do not visibly snap back while the
       drawer is still on screen, and it re-checks that the drawer is still
       closed in case it was reopened in the meantime. */
    function resetMobileMenuLevel(d) {
      if (!d) return;
      setTimeout(function () {
        if (d.open) return;
        var open = d.querySelectorAll('details.link-container');
        Array.prototype.forEach.call(open, function (lc) {
          lc.classList.remove('menu-opening');
          lc.open = false;
        });
      }, 420);
    }

    if (searchToggle) {
      /* THE BUTTON TOGGLES. It used to only open, which was survivable on
         desktop — the button collapses to `width: 0` there to make room for the
         field, exactly as the design does, and clicking away closes the panel.
         On a phone it was a dead end: the design KEEPS this button visible while
         search is open (`.topp[data-sok="1"] .sokknapp { width:auto; opacity:1 }`
         at <=920, the opposite of its desktop rule), and ours collapsed it at
         every width. With the button gone and the outside click not wired to the
         search either, an open search could not be closed at all. */
      searchToggle.addEventListener('click', function () {
        if (header.hasAttribute(SEARCH)) {
          closeSearch();
          var open = searchInput();
          if (open) open.blur();
          return;
        }
        closeCart();
        closeMobileMenu();
        header.setAttribute(SEARCH, '');
        searchToggle.setAttribute('aria-expanded', 'true');
        closeAll(null);
        syncEmptyState();
        var input = searchInput();
        if (input) input.focus();
        // A field that is already full opens straight onto its results.
        if (input) runSearch(input.value);
      });
    }

    /* "Populärt just nu" rows FILL the field rather than navigating, which is
       what the design's `data-sok-satt` does — sending the visitor to /search
       would close the panel they are still using. Delegated so it survives the
       empty state being re-rendered. */
    document.addEventListener('click', function (e) {
      var row = e.target.closest ? e.target.closest('[data-dalafisk-search-term]') : null;
      if (!row) return;
      e.preventDefault();
      var input = searchInput();
      if (!input) return;
      var term = row.getAttribute('data-dalafisk-search-term') || '';
      input.value = term;
      input.focus();
      runSearch(term);
    });

    document.addEventListener('cart-drawer:open', function () {
      closeSearch();
      closeMobileMenu();
      closeAll(null);
    });

    /* ---------- mobile menu: staggered rows ----------
       The design's `stegraRader()`: every row in the level that is coming into
       view gets its own animation-delay, so the level unfolds top to bottom
       instead of appearing as one block. `MENU_BASE` is the design's 200ms —
       long enough for the drawer to finish sliding in — and level changes
       INSIDE the drawer start at 0, because there is no slide to wait for.
       The 12-row cap is the design's `Math.min(i, 11)`; past that the tail
       would lag far enough to read as a stall rather than a stagger.

       The animation itself is in dalafisk-mobile-menu.css and already has a
       200ms default, so without this the level still fades in as a block. */
    var MENU_BASE = 200;
    var MENU_STEP = 24;

    function stagger(rows, base) {
      Array.prototype.forEach.call(rows, function (el, i) {
        el.style.animationDelay = (base + Math.min(i, 11) * MENU_STEP) + 'ms';
      });
    }

    /* Level 1 reads as one list even though it is not one element: the search
       button, the top-level items (one level down, inside the <ul>), then the
       CTA and the account block. Walking the body's children keeps them in
       document order without a selector that would also catch sub-menu rows. */
    function staggerLevelOne(drawer) {
      var body = drawer.querySelector('.dalafisk-mobile-menu__body');
      if (!body) return;
      var rows = [];
      Array.prototype.forEach.call(body.children, function (child) {
        if (child.classList.contains('dalafisk-mobile-menu__list')) {
          Array.prototype.forEach.call(child.children, function (li) { rows.push(li); });
        } else {
          rows.push(child);
        }
      });
      stagger(rows, MENU_BASE);
    }

    var mobileMenu = mobileMenuEl();
    if (mobileMenu) {
      mobileMenu.addEventListener('toggle', function () {
        if (!mobileMenu.open) return;
        closeSearch();
        closeCart();
        closeAll(null);
        staggerLevelOne(mobileMenu);
      });
    }

    /* Level 2. Pinnacle's header.js opens the <details> from the summary click;
       `toggle` does not bubble, so the same click is used here rather than a
       listener per <details> — the drawer markup is re-rendered by the theme
       editor and a delegated handler survives that. The back row is the level's
       own header and stays put, as `.drawer__topp` does in the design. */
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;
      var summary = e.target.closest('.dalafisk-mobile-menu .link-container > summary');
      if (!summary) return;
      var list = summary.parentElement.querySelector('.sub-menu');
      if (!list) return;
      stagger(list.querySelectorAll(':scope > li:not(.parent-link-back)'), 0);
    });

    /* The design's drawer has its own X and its own search row; Pinnacle's
       drawer has neither, so both are wired here. Delegated, because the drawer
       markup is re-rendered by the theme editor on every setting change. */
    document.addEventListener('click', function (e) {
      if (!e.target.closest) return;

      if (e.target.closest('[data-dalafisk-menu-close]')) {
        e.preventDefault();
        closeMobileMenu();
        return;
      }

      if (e.target.closest('[data-dalafisk-menu-search]')) {
        e.preventDefault();
        closeMobileMenu();
        /* Going through the toggle rather than setting the attribute keeps one
           owner of "open the search": focus, aria-expanded and the empty state
           are all in that handler. */
        if (searchToggle) searchToggle.click();
      }
    });

    if (searchForm) {
      searchForm.addEventListener('input', function () {
        /* Every keystroke means a fetch is coming 300ms behind it, so the wait
           starts here too — typing the first character hit the same blank panel
           as clicking a suggestion did. */
        var input = searchInput();
        if (input && input.value.trim() !== '') awaitResults();
        syncEmptyState();
      });
      var formEl = searchForm.querySelector('form');
      if (formEl) {
        formEl.addEventListener('submit', function () {
          var input = searchInput();
          if (input) rememberSearch(input.value);
        });
      }
    }

    /* Recording history on submit alone is not enough: pressing Return in
       Pinnacle's field does not fire a submit, and a visitor can also reach the
       results by clicking a predictive row or "Visa alla resultat". Reading the
       query back off the results URL catches every one of those paths. */
    if (/[?&]q=/.test(window.location.search)) {
      var q = decodeURIComponent(
        (window.location.search.split(/[?&]q=/)[1] || '').split('&')[0].replace(/\+/g, ' ')
      );
      rememberSearch(q);
    }

    /* A click anywhere outside the header collapses the search back to the icon,
       which is what the design does: it closes unless the click is inside .topp
       or inside .sokpanel. Here the panel is a descendant of the header, so one
       contains() test covers both.

       Two things this has to get right, and the previous version got neither:

       1. Pinnacle renders `.searchform--click-capture` — a full-viewport fixed
          overlay — as a CHILD of `.searchform`. So `searchForm.contains(target)`
          was true for EVERY click on the page while the search was open, and the
          handler bailed out every time. The search could never be dismissed by
          clicking away; it just sat there expanded and empty.
       2. It also refused to close while the field had text in it. The design has
          no such exception — clicking away always collapses. */
    document.addEventListener('click', function (e) {
      if (!header.hasAttribute(SEARCH)) return;
      var onCapture = e.target.closest && e.target.closest('.searchform--click-capture');
      if (!onCapture && header.contains(e.target)) return;
      closeSearch();
    });

    /* ---------- shrink on scroll ----------
       Two thresholds, not one. With a single line at 40px a page that settles
       right on it — iOS overscroll, a slow thumb, a scroll-snap — flips the
       shrunk state back and forth and restarts the .25s transitions each time,
       which reads as stutter. Shrinking at 48 and un-shrinking at 16 leaves a
       32px dead band a settling page never crosses twice. The style cost of
       each flip is small since custom-header-style.css scoped the shrunk
       variable to the header (2026-09-22), but a flip that never happens is
       cheaper still. */
    var SHRINK_AT = 48;
    var EXPAND_AT = 16;
    var ticking = false;

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        var y = window.scrollY;
        if (y > SHRINK_AT) header.setAttribute(SHRUNK, '');
        else if (y < EXPAND_AT) header.removeAttribute(SHRUNK);
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor re-renders the header section on every setting change.
  document.addEventListener('shopify:section:load', function () {
    window.__dalafiskHeader = false;
    init();
    window.__dalafiskHeader = true;
  });
})();
